'use client'

import { useEffect, useState } from 'react'
import { CheckIcon, CopyIcon } from '@/components/icons/icon-set'
import { cn } from '@/lib/utils'

interface CodeBlockProps {
    code: string
    title?: string
    className?: string
    maxHeight?: number
}

/* Docs code block: monospace on a quiet surface with a copy action. */
export function CodeBlock({ code, title, className, maxHeight }: CodeBlockProps) {
    const [copied, setCopied] = useState(false)

    useEffect(() => {
        if (!copied) return
        const timer = window.setTimeout(() => setCopied(false), 1600)
        return () => window.clearTimeout(timer)
    }, [copied])

    const copy = () => {
        navigator.clipboard?.writeText(code).then(() => setCopied(true)).catch(() => undefined)
    }

    return (
        <div data-slot='code-block' className={cn('relative min-w-0 overflow-hidden rounded-xl border border-border-subtle bg-surface-subtle dark:bg-surface-raised', className)}>
            {title && (
                <div className='flex h-10 items-center border-b border-border-subtle px-4 font-mono text-xs text-text-secondary'>
                    <span className='truncate'>
                        {title}
                    </span>
                </div>
            )}
            <button
                type='button'
                onClick={copy}
                aria-label={copied ? 'Copied' : 'Copy code'}
                className={cn(
                    'absolute right-2 z-10 inline-flex size-8 cursor-pointer items-center justify-center rounded-md text-icon-secondary transition-colors outline-none hover:bg-action-secondary-hover hover:text-icon-hover focus-visible:outline-2 focus-visible:outline-border-focus [&_svg]:size-4',
                    title ? 'top-1' : 'top-2'
                )}
            >
                {copied ? <CheckIcon className='text-icon-brand' aria-hidden='true' /> : <CopyIcon aria-hidden='true' />}
            </button>
            <pre className='overflow-auto p-4 pr-12 font-mono text-sm leading-6 text-text-primary' style={maxHeight ? { maxHeight } : undefined}>
                <code>
                    {code}
                </code>
            </pre>
        </div>
    )
}
